import React from 'react'
import {Card, ColorSquare, Label, Tooltip} from '@therms/atalaya'
import {
  DateTime,
  IdLabel,
  LocationNames,
  RefsByTypeTitles,
  ReportStateBadge,
  RPCDocs,
  Tags,
  UserNames,
} from '@therms/react-modules'

interface ReportListCardProps {
  activityTypeTags?: RPCDocs.Reports.GetReportsList.v1.ActivityTypeTag[]
  authors?: RPCDocs.Reports.GetReportsList.v1.Author[]
  date: string
  description?: string
  idLabel?: string
  onClick: () => void
  refsByType?: RPCDocs.Reports.GetReportsList.v1.RefsByType
  reportType: RPCDocs.Reports.GetReportsList.v1.ReportType
  state: RPCDocs.Reports.GetReportsList.v1.State
  status: RPCDocs.Reports.GetReportsList.v1.Status
  subTitle?: string
  title: string
}

function ReportListCard({
  activityTypeTags,
  authors,
  date,
  description,
  idLabel,
  onClick,
  refsByType,
  reportType,
  state,
  status,
  subTitle,
  title,
}: ReportListCardProps) {
  return (
    <Card onClick={onClick} space="xs">
      <div className="gap-x-xs inline-flex w-full flex-wrap justify-between">
        <div className="space-x-xs">
          {!!reportType.color && <ColorSquare color={reportType.color} />}

          <span>{reportType.name}</span>

          <IdLabel
            className="text-color-subtle text-sm font-bold tracking-wide"
            idLabel={idLabel}
          />
        </div>

        <div className="truncate">
          {status.title} <ReportStateBadge state={state} />
        </div>
      </div>

      <div className="inline-flex w-full flex-wrap justify-between">
        {!!activityTypeTags && !!activityTypeTags.length && (
          <Tags tags={activityTypeTags} max={5} />
        )}

        {!!refsByType?.locationIds?.length && (
          <div className="text-neutral flex grow justify-end">
            <Tooltip
              tooltip={
                refsByType?.locationIds.length > 1 && (
                  <LocationNames locationIds={refsByType?.locationIds} />
                )
              }
              underline={refsByType?.locationIds.length > 1}
            >
              <LocationNames
                locationIds={refsByType?.locationIds}
                listAll={false}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div className="border-border border-t" />

      <div className="w-full">
        <div className="gap-x-xs inline-flex w-full flex-wrap flex-wrap-reverse justify-between">
          <div className="text-color-strong truncate font-bold">{title}</div>

          <span className="text-color-subtle">
            <DateTime mode="date" timestamp={date} />
          </span>
        </div>

        {refsByType && (
          <div className="pb-xs text-color-subtle text-sm">
            <RefsByTypeTitles refsByType={refsByType} showDetails={false} />
          </div>
        )}
      </div>

      <div className="gap-x-xs inline-flex flex-wrap justify-between">
        <div className="space-x-xxs truncate">
          <Label>Authors</Label>

          <UserNames userIds={authors?.map((a) => a.id)} />
        </div>

        <div className="text-color-subtle">{description}</div>
      </div>
    </Card>
  )
}

export {ReportListCard}
